/* @flow */

import moment from 'moment';
import { createSelector } from 'reselect';
import { type State } from '../preact-app/types';
import { type Funcionamento } from '../funcionamento/types';
import { type FuncionamentoState } from './funcionamento-types';
import api from '../utils/api';
import { MOMENT_FORMATO_BANCO } from '../utils/constants';
import { AUTENTICAR, type AutenticarAction } from './usuario';
import formatarFuncionamento from '../funcionamento/formatar-funcionamento';
import { getRelogioFormatoBanco } from './relogio';

type SetFuncionamentoAction = {
    type: 'empresa/SET_FUNCIONAMENTO',
    funcionamento: Funcionamento[],
};

type AdicionarFuncionamentoAction = {
    type: 'empresa/ADICIONAR_FUNCIONAMENTO',
    funcionamento: Funcionamento,
};

type EditarFuncionamentoAction = {
    type: 'empresa/EDITAR_FUNCIONAMENTO',
    funcionamento: Funcionamento,
};

type RemoverFuncionamentoAction = {
    type: 'empresa/REMOVER_FUNCIONAMENTO',
    id: number,
};

type Action =
    | SetFuncionamentoAction
    | AdicionarFuncionamentoAction
    | EditarFuncionamentoAction
    | RemoverFuncionamentoAction
    | AutenticarAction;

// Action Types

const SET_FUNCIONAMENTO = 'empresa/SET_FUNCIONAMENTO';
const ADICIONAR_FUNCIONAMENTO = 'empresa/ADICIONAR_FUNCIONAMENTO';
const EDITAR_FUNCIONAMENTO = 'empresa/EDITAR_FUNCIONAMENTO';
const REMOVER_FUNCIONAMENTO = 'empresa/REMOVER_FUNCIONAMENTO';

// Action Creators


export const setFuncionamento = (funcionamento: Funcionamento[]): SetFuncionamentoAction => ({
    type: SET_FUNCIONAMENTO,
    funcionamento,
});

export const adicionarFuncionamento: Funcionamento => AdicionarFuncionamentoAction = (
    funcionamento => ({ type: ADICIONAR_FUNCIONAMENTO, funcionamento })
);

export const editarFuncionamento: Funcionamento => EditarFuncionamentoAction = (
    funcionamento => ({ type: EDITAR_FUNCIONAMENTO, funcionamento })
);

export const removerFuncionamento = (id: number): RemoverFuncionamentoAction => ({
    type: REMOVER_FUNCIONAMENTO,
    id,
});

// Selectors

export const getFuncionamento = (state: State) => state.funcionamento;


const isHoraDoFuncionamento = (horas: string) => ({ horaInicio, horaFim }) => (
    moment(horas, MOMENT_FORMATO_BANCO).isBetween(
        moment(horaInicio, 'HH:mm:ss'),
        moment(horaFim, 'HH:mm:ss'),
    )
);

const isHoraAntesDoInicio = (horas: string) => ({ horaInicio }) => (
    moment(horas, MOMENT_FORMATO_BANCO).isBefore(moment(horaInicio, 'HH:mm:ss'))
);

const getDiaSemanaIdAtual = createSelector(
    getRelogioFormatoBanco,
    data => moment(data).day() + 1,
);

export const getFuncionamentosDoDiaDaSemana: State => Funcionamento[] = createSelector(
    getFuncionamento,
    getDiaSemanaIdAtual,
    (funcionamento, diaSemanaIdAtual) => funcionamento
        .filter(({ diaSemanaId }) => diaSemanaId === diaSemanaIdAtual),
);

export const getFuncionamentoAtual: State => ?Funcionamento = createSelector(
    getFuncionamentosDoDiaDaSemana,
    getRelogioFormatoBanco,
    (funcionamento, ultimaVerificacao) => (
        funcionamento.find(isHoraDoFuncionamento(ultimaVerificacao))
    ),
);

export const getDescricaoFuncionamentoAtual: State => ?string = createSelector(
    getFuncionamentoAtual,
    funcionamento => funcionamento && formatarFuncionamento(funcionamento),
);

export const isEmFuncionamento: State => boolean = createSelector(getFuncionamentoAtual, Boolean);

export const getProximoFuncionamento: State => ?Funcionamento = createSelector(
    getFuncionamentosDoDiaDaSemana,
    getRelogioFormatoBanco,
    (funcionamento, hora) => funcionamento.find(isHoraAntesDoInicio(hora)),
);

export const getHoraAbertura: State => ?string = createSelector(
    getProximoFuncionamento,
    funcionamento => funcionamento && moment(funcionamento.horaInicio, 'HH:mm:ss').format('H:mm'),
);

// Thunks

export const buscarFuncionamento = () => (dispatch: Function) => api
    .getFuncionamento()
    .then(({ data }) => dispatch(setFuncionamento(data)));

const initialState: FuncionamentoState = [];

type FuncionamentoReducer = (state: FuncionamentoState, action: Action) => FuncionamentoState;

const funcionamentoReducer: FuncionamentoReducer = (state = initialState, action) => {
    switch (action.type) {
    case SET_FUNCIONAMENTO:
        return action.funcionamento;
    case ADICIONAR_FUNCIONAMENTO:
        return [...state, action.funcionamento];
    case EDITAR_FUNCIONAMENTO: {
        const { funcionamento } = action;
        return state.map(f => (f.id === funcionamento.id ? funcionamento : f));
    }
    case REMOVER_FUNCIONAMENTO: {
        const { id } = action;
        return state.filter(f => f.id !== id);
    }
    case AUTENTICAR:
        return initialState;
    default:
        return state;
    }
};

export default funcionamentoReducer;
